// Shared product helpers for the product list, detail panel and sales pages.
// Several of these mirror logic in the iOS app (shopik-poc) so both clients
// agree on things like "is this listed" and the condition price tiers.

import { constants, conditionDisplayLabel } from '../productEditConstants';

// ── Channels ─────────────────────────────────────────────────────────────────

export const CHANNEL_LABELS = {
  ebay: 'eBay',
  shopify: 'Shopify',
  square: 'Square',
  etsy: 'Etsy',
  googleMerchant: 'Google Merchant',
  google_merchant: 'Google Merchant',
  off_platform: 'Offline',
};

export const channelLabel = (channel) => {
  if (!channel) return 'Offline';
  return CHANNEL_LABELS[channel] || CHANNEL_LABELS[channel.toLowerCase()] || channel.charAt(0).toUpperCase() + channel.slice(1);
};

// Mirrors Product.isOnEBay / isOnShopify / … in shopik-poc/Models/Product.swift.
export function isListedOnChannel(product, channelKey) {
  if (!product) return false;
  if (channelKey === 'shopify') return !!product.shopifyData?.length;
  if (channelKey === 'ebay') return !!product.ebayData?.some?.((e) => e?.listingId);
  if (channelKey === 'square') return !!product.squareData?.length;
  if (channelKey === 'etsy') return !!product.etsyData?.length;
  if (channelKey === 'googleMerchant') return !!product.googleMerchantData?.length;
  return false;
}

export const LISTING_CHANNELS = ['ebay', 'shopify', 'square', 'etsy', 'googleMerchant'];

export const isListedAnywhere = (product) => LISTING_CHANNELS.some((c) => isListedOnChannel(product, c));

export const listedChannels = (product) => LISTING_CHANNELS.filter((c) => isListedOnChannel(product, c));

export const customerHasChannel = (customer, channelKey) => {
  const destinations = customer?.destinations || [];
  switch (channelKey) {
    case 'shopify': return destinations.some((d) => d.type === 1);
    case 'ebay': return destinations.some((d) => d.type === 2);
    case 'googleMerchant': return destinations.some((d) => d.type === 3 && d.storeId && d.storeKey);
    case 'etsy': return destinations.some((d) => d.type === 5);
    case 'square': return !!customer?.squareSettings;
    default: return false;
  }
};

// Same pre-flight as Customer.missingEtsySettingsFields() on iOS — names the
// settings Etsy will reject a listing without, instead of surfacing Etsy's raw
// "IDs must be >= 1, got 0" 400.
export function missingEtsySettings(customer, product) {
  const etsy = (customer?.destinations || []).find((d) => d.type === 5);
  const settings = etsy?.etsySettings || {};
  const missing = [];
  if (!settings.shippingProfileId) missing.push('shipping profile');
  if (!settings.readinessStateId) missing.push('processing profile');
  if (!settings.returnPolicyId) missing.push('return policy');
  if (!product?.etsyTaxonomyId && !settings.taxonomyId) missing.push('category');
  return missing;
}

// Channel endpoints return the adapter's `data` verbatim on failure — sometimes
// a plain string, sometimes `{ error }`.
export function channelErrorText(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  if (data?.message) return data.message;
  return fallback || err?.message || 'Something went wrong';
}

// Parses eBay's "<Field Name> (<aspect id>) is a required field." errors (there
// can be several) into just the field names — same regex as iOS
// ProductDataModel.missingRequiredEbayFields(fromRawError:).
export function missingRequiredEbayFields(rawError) {
  if (!rawError) return [];
  const re = /([A-Za-z0-9 &/'-]+?)\s*\(\d+\)\s*is a required field/g;
  const fields = [];
  let match;
  while ((match = re.exec(rawError)) !== null) {
    const field = match[1].trim();
    if (field && !fields.includes(field)) fields.push(field);
  }
  return fields;
}

// ── Prices ───────────────────────────────────────────────────────────────────

export function parsePriceValue(price) {
  if (price == null) return null;
  if (price === 'FREE') return 0;
  if (price === 'Unknown') return null;
  const n = parseFloat(String(price).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(n) ? null : n;
}

export const formatCurrency = (value, { whole = false } = {}) => {
  const n = Number(value) || 0;
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
};

// Condition price tiers — a direct port of PriceTier in
// shopik-poc/Views/PriceConditionDrawerView.swift. Keep the numbers in sync.
export const PRICE_TIERS = [
  { key: 'excellent', label: 'Excellent', sublabel: 'Like new, minimal to no wear', percentage: 1.0, fallbackHalfWidth: 0.045, spreadMultiplier: 0.75 },
  { key: 'good', label: 'Good', sublabel: 'Light wear, normal use', percentage: 0.95, fallbackHalfWidth: 0.05, spreadMultiplier: 0.85 },
  { key: 'fair', label: 'Fair', sublabel: 'Visible wear, minor flaws', percentage: 0.85, fallbackHalfWidth: 0.065, spreadMultiplier: 1.1 },
  { key: 'poor', label: 'Poor', sublabel: 'Heavy wear, noticeable flaws', percentage: 0.70, fallbackHalfWidth: 0.095, spreadMultiplier: 1.5 },
];

const TIER_BY_CONDITION = {
  NEW: 'excellent', NEW_OTHER: 'excellent', CERTIFIED_REFURBISHED: 'excellent', EXCELLENT_REFURBISHED: 'excellent',
  LIKE_NEW: 'excellent', PRE_OWNED_EXCELLENT: 'excellent', USED_EXCELLENT: 'excellent',
  NEW_WITH_DEFECTS: 'good', VERY_GOOD_REFURBISHED: 'good', GOOD_REFURBISHED: 'good', SELLER_REFURBISHED: 'good', USED_VERY_GOOD: 'good',
  PRE_OWNED_FAIR: 'fair', USED_GOOD: 'fair',
  USED_ACCEPTABLE: 'poor', FOR_PARTS_OR_NOT_WORKING: 'poor',
};

export const tierForCondition = (condition) => {
  const key = TIER_BY_CONDITION[condition] || 'excellent';
  return PRICE_TIERS.find((t) => t.key === key);
};

// eBay's ungraded "Card Condition" vocabulary — for an ungraded trading card
// this, not Condition (pinned to eBay's single "Ungraded" id), carries the
// card's condition. Mirrors node-core/src/pricingTiers.js.
const TIER_BY_CARD_CONDITION = {
  'near mint or better': 'excellent',
  excellent: 'good',
  'very good': 'fair',
  poor: 'poor',
};

export const isGradedCard = (specs) => String(specs?.Graded || '').toLowerCase() === 'yes';

// The tier a product's specs put it in. A graded card is priced for its exact
// grade by the backend, so it always sits at 100%. Same rules as
// node-core/src/pricingTiers.js's priceTierForProduct.
export function tierForSpecs(specs = {}) {
  if (isGradedCard(specs)) return PRICE_TIERS[0];
  const cardKey = TIER_BY_CARD_CONDITION[String(specs['Card Condition'] || '').toLowerCase()];
  if (cardKey) return PRICE_TIERS.find((t) => t.key === cardKey);
  return tierForCondition(specs.Condition);
}

// The condition-neutral (100% tier) market value the tiers multiply.
// priceOptions.marketValue is written by the Node pipeline / price
// regeneration; older products only have priceOptions.shopik, which already
// reflects the condition the item was priced at, so back the tier out of it.
export function marketValueFor(product, savedSpecs = product?.specs) {
  const stored = parsePriceValue(product?.priceOptions?.marketValue);
  if (stored != null && stored > 0) return stored;
  const priced = parsePriceValue(product?.priceOptions?.shopik ?? savedSpecs?.Price ?? product?.price) || 0;
  const tier = tierForSpecs(savedSpecs || {});
  return tier.percentage > 0 ? priced / tier.percentage : priced;
}

// Edits to these fields move the price along the condition tiers.
export const CONDITION_TIER_FIELDS = ['Condition', 'Card Condition'];
// Edits to these change what a graded card is worth in ways a tier
// percentage can't model (a PSA 10 vs a PSA 9), so they re-run pricing.
export const GRADE_REPRICE_FIELDS = ['Grade', 'Professional Grader', 'Graded'];

// Band centered on an already-known price, scaled by the backend's comp spread
// (PriceLow/PriceHigh) when present, otherwise a fixed per-tier width.
export function tierBand(tier, value, priceLow, priceHigh) {
  let halfWidthFraction;
  if (priceLow != null && priceHigh != null && value > 0 && priceHigh > priceLow) {
    halfWidthFraction = ((priceHigh - priceLow) / (2 * value)) * tier.spreadMultiplier;
  } else {
    halfWidthFraction = tier.fallbackHalfWidth;
  }
  const halfWidth = value * halfWidthFraction;
  return { low: Math.max(0, value - halfWidth), high: value + halfWidth };
}

export const tierRange = (tier, marketValue, priceLow, priceHigh) =>
  tierBand(tier, marketValue * tier.percentage, priceLow, priceHigh);

// Full spread across all tiers, reconstructed from the current price and the
// tier it corresponds to so an already-discounted price isn't discounted twice.
export function overallPriceRange(currentPrice, currentTier, priceLow, priceHigh) {
  const marketValue = currentTier.percentage > 0 ? currentPrice / currentTier.percentage : currentPrice;
  const ranges = PRICE_TIERS.map((t) => tierRange(t, marketValue, priceLow, priceHigh));
  return { low: Math.min(...ranges.map((r) => r.low)), high: Math.max(...ranges.map((r) => r.high)) };
}

// "$280.00 ($240 – $320)" — the Market Value line on iOS.
export function marketValueText(product) {
  const specs = product?.specs || {};
  const current = parsePriceValue(specs.Price ?? product?.price);
  if (current == null || current <= 0) return product?.price && product.price !== 'Unknown' ? product.price : null;
  const range = overallPriceRange(
    current,
    tierForSpecs(specs),
    parsePriceValue(specs.PriceLow),
    parsePriceValue(specs.PriceHigh),
  );
  return `${formatCurrency(current)} (${formatCurrency(range.low, { whole: true })} – ${formatCurrency(range.high, { whole: true })})`;
}

// ── Stored dates ("yyyyMMdd,HH:mm:ss+0000", UTC) ─────────────────────────────

export function parseStoredDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})(\d{2})(\d{2}),(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)));
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatStoredDate(date = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}${p(date.getUTCMonth() + 1)}${p(date.getUTCDate())},${p(date.getUTCHours())}:${p(date.getUTCMinutes())}:${p(date.getUTCSeconds())}+0000`;
}

// Local calendar day ("YYYY-MM-DD") for a stored timestamp — what the date
// filters compare against the browser's <input type="date"> values.
export function storedDateDay(value) {
  const d = parseStoredDate(value);
  if (!d) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function displayDateTime(value) {
  const d = parseStoredDate(value);
  if (!d) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Same rule as Product.didFailProcessing() on iOS: not finished, still no title,
// and neither created nor retried within the last 3 minutes.
export function didFailProcessing(product, titleValue) {
  if (!product || product.finishedProcessing || titleValue) return false;
  const recent = (value) => {
    const d = parseStoredDate(value);
    return d && (Date.now() - d.getTime()) < 180 * 1000;
  };
  return !recent(product.created) && !recent(product.lastRetry);
}

// ── Misc ─────────────────────────────────────────────────────────────────────

const isPlaceholderValue = (v) => !v || constants.EXCLUDE_FROM_PRODUCT_TERMS.includes(String(v).trim().toLowerCase());
export { isPlaceholderValue };

// Same layout as Product.clipboardData on iOS: title, description, price,
// category, then the remaining specs with placeholder values last.
export function buildClipboardText(product) {
  const specs = product?.specs || {};
  const lines = [
    specs.Title || product?.title || '',
    specs.Description || product?.description || '',
    specs.Price ? `$${String(specs.Price).replace(/^\$/, '')}` : (product?.price || ''),
    `Category: ${specs.Category || product?.gptCategories?.primaryCategory || ''}`,
  ];
  const skip = new Set(['title', 'description', 'price', 'primarycategory', 'category']);
  const after = [];
  Object.entries(specs).forEach(([key, value]) => {
    if (skip.has(key.toLowerCase())) return;
    const text = key === 'Condition' ? conditionDisplayLabel(value) : value;
    if (isPlaceholderValue(value)) after.push(`${key}: ${text ?? ''}`);
    else lines.push(`${key}: ${text}`);
  });
  return [...lines, ...after].join('\n');
}

// "PrimaryCategory" → "Primary Category", matching iOS fieldLabel(key:).
export const humanizeKey = (key) => String(key).replace(/([a-z])([A-Z])/g, '$1 $2');
